import React, { useEffect, useMemo, useState } from 'react';
import LabeledField from '../../InquiryRotationView/InquirySchemeManagement/components/LabeledField';
import {
  DatePickerComponent,
  Inputs,
  SelectComponet,
} from '../../../../Components';
import { Button } from '@material-ui/core';
import { LookupSelect } from '../../ActivitiesTypeView/ActivitiesTypeManagementView/DialogManagementViewComponent/DialogManagementConrtolComponent/LookupSelect';
import { useTranslation } from 'react-i18next';
import { PaymentPlansLocalePath } from '../i18n/PaymentPlansLocale';
import { Add } from '@material-ui/icons';
import { GetLookupItemsByLookupTypeName } from '../../../../Services';
import {
  generateUniqueKey,
  getErrorByName,
  showError,
} from '../../../../Helper';
import { PropertyPaymentPlan } from '../../../../Enums';
import moment from 'moment';
import Joi from 'joi';
import { config } from '../../../../config';
import { frequencyTypes, postHandoverFrequencyTypes } from '../constants';

const initialInstallment = (frequencyId) => ({
  installmentTypeId: config.InstallmentTypes.PostHandoverInstallment,
  amountPercentage: '',
  frequencyId,
  frequencyName: 'month',
  frequencyValue: '',
  numberOfInstallment: null,
  id: crypto.randomUUID(),
});
const PostInstallmentItem = ({ dispatch }) => {
  const { t } = useTranslation(PaymentPlansLocalePath);
  const [installment, setInstallment] = useState(initialInstallment);
  const [frequencies, setFrequencies] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [key, setKey] = useState(generateUniqueKey);
  const onFrequencyToggleHandler = (value) => {
    let frequencyId;
    switch (value) {
      case postHandoverFrequencyTypes.BY_MONTH:
        frequencyId = monthFrequencyId;
        break;
      case postHandoverFrequencyTypes.BY_FIXED_DATE:
        frequencyId = dateFrequencyId;
        break;
      case postHandoverFrequencyTypes.BY_DURATION:
        frequencyId = monthFrequencyId;
        break;
      default:
        break;
    }
    const frequencyName = frequencies
      .find((frequency) => frequency.lookupItemId === frequencyId)
      ?.lookupItemName.toLowerCase();
    setInstallment((prevInstallment) => ({
      ...prevInstallment,
      frequencyId,
      frequencyName,
      numberOfInstallment:
        value === postHandoverFrequencyTypes.BY_MONTH ? 1 : null,
    }));
  };
  const dateFrequencyId = useMemo(
    () =>
      frequencies.find(
        (frequency) => frequency.lookupItemName.toLowerCase() === 'date'
      )?.lookupItemId,
    [frequencies]
  );
  const monthFrequencyId = useMemo(
    () =>
      frequencies.find(
        (frequency) => frequency.lookupItemName.toLowerCase() === 'month'
      )?.lookupItemId,
    [frequencies]
  );
  const yearFrequencyId = useMemo(
    () =>
      frequencies.find(
        (frequency) => frequency.lookupItemName.toLowerCase() === 'year'
      )?.lookupItemId,
    [frequencies]
  );

  const isDuration = useMemo(
    () =>
      [yearFrequencyId, monthFrequencyId].includes(installment.frequencyId) &&
      !installment.numberOfInstallment,
    [
      frequencies,
      yearFrequencyId,
      monthFrequencyId,
      installment.frequencyId,
      installment.numberOfInstallment,
    ]
  );

  const updateInstallmentHandler = (field, value) => {
    if (field === 'numberOfInstallment' && +value <= 0) {
      value = 1;
    }
    if (field === 'frequencyId') {
      const frequencyName =
        frequencies
          .find((frequency) => frequency.lookupItemId === value)
          ?.lookupItemName.toLowerCase() ?? 'month';
      return setInstallment((prevInstallment) => ({
        ...prevInstallment,
        frequencyId: value,
        frequencyName,
      }));
    }
    setInstallment((prevInstallment) => ({
      ...prevInstallment,
      [field]: value,
    }));
  };
  const installmentSchema = Joi.object({
    installmentTypeId: Joi.number().required(),
    amountPercentage: Joi.number()
      .required()
      .min(1)
      .max(99)
      .messages({
        'number.min': t('installmentAmountPercentageMin'),
        'number.max': t('installmentAmountPercentageMax'),
        'number.base': t('is-required', {
          field: t('amount_percentage'),
          ns: 'InquiryRotation',
        }),
      }),
    frequencyId: Joi.number().required(),
    frequencyValue: Joi.string()
      .required()
      .custom((value, helper) => {
        if (+value) {
          if (!Number.isInteger(+value)) {
            return helper.error('string.decimal');
          } else if (+value < 1) {
            return helper.error('string.negative');
          }
        }
      })
      .messages({
        'string.required': t('is-required', {
          field: t('duration'),
          ns: 'InquiryRotation',
        }),
        'string.base': t('is-required', {
          field: t('duration'),
          ns: 'InquiryRotation',
        }),
        'string.empty': t('is-required', {
          field: t('duration'),
          ns: 'InquiryRotation',
        }),
        'string.negative': t('valueMustBePositive'),
        'string.decimal': t('string_decimal'),
      }),
    numberOfInstallment: Joi.custom(() => {
      if (getSelectedTriggerType() === postHandoverFrequencyTypes.BY_MONTH) {
        return Joi.number()
          .required()
          .min(1)
          .max(99)
          .messages({
            'number.min': t('installmentAmountPercentageMin'),
            'number.max': t('installmentAmountPercentageMax'),
            'number.base': t('is-required', {
              field: t('number_of_installment'),
              ns: 'InquiryRotation',
            }),
          });
      } else {
        return Joi.number().allow(null);
      }
    }),
  }).validate(installment, { allowUnknown: true, abortEarly: false });

  const onAddHandler = () => {
    setIsSubmitted(true);
    if (installmentSchema.error) {
      return;
    }
    installment.amountPercentage = +installment.amountPercentage / 100;
    dispatch({ field: 'postHandoverInstallment', value: installment });
    setInstallment(initialInstallment(monthFrequencyId));
    setIsSubmitted(false);
    setKey(generateUniqueKey());
  };

  function getSelectedTriggerType() {
    if (installment.numberOfInstallment)
      return postHandoverFrequencyTypes.BY_MONTH;
    else if (installment.frequencyId === dateFrequencyId) {
      return postHandoverFrequencyTypes.BY_FIXED_DATE;
    } else return postHandoverFrequencyTypes.BY_DURATION;
  }

  useEffect(() => {
    const getFrequencies = async () => {
      try {
        const res = await GetLookupItemsByLookupTypeName({
          lookUpName: PropertyPaymentPlan.Frequency.lookupTypeName,
          pageIndex: 1,
          pageSize: 100,
        });
        updateInstallmentHandler(
          'frequencyId',
          res.result.find(
            (item) => item.lookupItemName.toLowerCase() === 'month'
          )?.lookupItemId
        );
        setFrequencies(res.result);
      } catch (error) {
        showError('error');
      }
    };
    getFrequencies();
  }, []);

  return (
    <div key={key}>
      <LabeledField
        className='gap-32'
        label={t('post_handover_installment')}
        component={
          <Inputs
            startAdornment='%'
            inputPlaceholder={'(e.g., 25%)'}
            onInputChanged={(e) =>
              updateInstallmentHandler('amountPercentage', e.target.value)
            }
            value={installment.amountPercentage}
            isWithError
            helperText={
              getErrorByName(installmentSchema, 'amountPercentage').message
            }
            error={getErrorByName(installmentSchema, 'amountPercentage').error}
            isSubmitted={isSubmitted}
          />
        }
      />
      <LabeledField
        className='gap-32'
        label={t('trigger_type')}
        component={
          <div className='flex gap-12 flex-col fa-start px-3'>
            <SelectComponet
              data={Object.values(postHandoverFrequencyTypes)}
              onSelectChanged={onFrequencyToggleHandler}
              multiple={false}
              value={getSelectedTriggerType()}
            />
          </div>
        }
      />
      {isDuration && (
        <LabeledField
          className='gap-32'
          label={t('by_duration')}
          component={
            <div className='flex- flex-col gap-6'>
              <Inputs
                type={'number'}
                startAdornment={
                  <LookupSelect
                    lookupName={'frequency'}
                    className={'m-0'}
                    onSelectChanged={(value) =>
                      updateInstallmentHandler('frequencyId', value)
                    }
                    excludeLookupByName={[
                      'date',
                      'day',
                      'completion',
                    ]}
                    value={installment.frequencyId}
                    renderValue={(_value, item) => (
                      <strong>{item?.lookupItemName}</strong>
                    )}
                  />
                }
                inputPlaceholder={'(e.g., 12 months)'}
                value={installment.frequencyValue}
                onInputChanged={(e) =>
                  updateInstallmentHandler('frequencyValue', e.target.value)
                }
                isWithError
                helperText={
                  isSubmitted &&
                  getErrorByName(installmentSchema, 'frequencyValue').message
                }
                error={
                  isSubmitted &&
                  getErrorByName(installmentSchema, 'frequencyValue').error
                }
                isSubmitted={isSubmitted}
              />
              <span className='helper-text'>{t('select_timeframe')}</span>
            </div>
          }
        />
      )}
      {installment.frequencyId === dateFrequencyId && (
        <LabeledField
          className='gap-32'
          label={t('by_specific_date')}
          component={
            <div className='flex- flex-col gap-6'>
              <DatePickerComponent
                onDateChanged={(e) =>
                  updateInstallmentHandler(
                    'frequencyValue',
                    new Date(e._d).toLocaleDateString()
                  )
                }
                value={moment(installment.frequencyValue)}
                helperText={
                  getErrorByName(installmentSchema, 'frequencyValue').message
                }
                error={
                  getErrorByName(installmentSchema, 'frequencyValue').error
                }
                isSubmitted={isSubmitted}
              />
              <span className='helper-text'>
                {t('select_installment_date')}
              </span>
            </div>
          }
        />
      )}
      {!!installment.numberOfInstallment && (
        <>
          <LabeledField
            className='gap-32'
            label={t('number_of_installment')}
            component={
              <Inputs
                type='number'
                inputPlaceholder={'Enter (e.g., 10)'}
                value={installment.numberOfInstallment}
                onInputChanged={(e) =>
                  updateInstallmentHandler(
                    'numberOfInstallment',
                    e.currentTarget.value
                  )
                }
                isWithError
                helperText={
                  getErrorByName(installmentSchema, 'numberOfInstallment')
                    .message
                }
                error={
                  getErrorByName(installmentSchema, 'numberOfInstallment').error
                }
                isSubmitted={isSubmitted}
              />
            }
          />
          <LabeledField
            className='gap-32'
            label={t('frequency')}
            component={
              <div className='flex- flex-col gap-6'>
                <Inputs
                  type='number'
                  inputPlaceholder={'(e.g., every 1, 2 months)'}
                  startAdornment={
                    <span className='start-adornment'>{t('months')}</span>
                  }
                  value={installment.frequencyValue}
                  onInputChanged={(e) =>
                    updateInstallmentHandler(
                      'frequencyValue',
                      e.currentTarget.value
                    )
                  }
                  isWithError
                  helperText={
                    getErrorByName(installmentSchema, 'frequencyValue').message
                  }
                  error={
                    getErrorByName(installmentSchema, 'frequencyValue').error
                  }
                  isSubmitted={isSubmitted}
                />
                <span className='helper-text'>{t('by_month_text')}</span>
              </div>
            }
          />
        </>
      )}
      <LabeledField
        hideDivider
        className='gap-32'
        component={
          <Button className=' capitalize flex gap-2' onClick={onAddHandler}>
            {' '}
            <Add />
            <span>{t('add_another')}</span>
          </Button>
        }
      />
    </div>
  );
};

export default PostInstallmentItem;
